<script setup lang="ts">
import RevisionGatedButton from "@/pages/ProjectVersionHistory/components/revisions/RevisionGatedButton.vue";
import DateInput from "@/components/Reusables/Inputs/DateInput.vue";
import NumberInput from "@/components/Reusables/Inputs/NumberInput.vue";
import SelectInputWithKeyValue from "@/components/Reusables/Inputs/SelectInputWithKeyValue.vue";
import TextInput from "@/components/Reusables/Inputs/TextInput.vue";
import { ProjectWasteMapping_Insert_Input } from "@/graphql";
import type { MaterialWasteSourceFragment } from "@/graphql";
import type { WastePickup, WasteTypeGroup } from "@/pages/WasteSorting/types/types";
import { useProjectEditReadOnly } from "@/pages/ProjectVersionHistory/composables/useProjectEditReadOnly.composable";
import { useModalStore } from "@/stores/modals/modals.store";
import type { EmissionCalculationPrerequisites } from "@/utils/calculateEmissionsByDenominator";
import UnitId from "@/features/ConstructionAmountCalculator/constants/UnitId";
import format from "@/services/Utils/format";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";

const props = defineProps<{
  pickup: WastePickup;
  group: WasteTypeGroup;
  onSubmit: (item: ProjectWasteMapping_Insert_Input) => Promise<unknown>;
  onDelete: (id: string) => Promise<void>;
  projectUnits: {
    originalEmission: string;
    scaledEmission: string;
  };
  emissionPrerequisites: EmissionCalculationPrerequisites;
  materialWeight?: number;
  wasteSource?: MaterialWasteSourceFragment;
  weightUnit?: "kg" | "ton";
}>();

const modals = useModalStore();
const route = useRoute();

// The modal still OPENS for a Viewer and while a frozen revision is previewed — reading a sorting
// is what both still allow — but it opens read-only: the fields go inert and Save / Delete refuse,
// since either would be turned down by Hasura's row permissions or would edit the current
// revision's rows. The footer buttons take the same guard, so their tooltip (at "top", clearing the
// footer) names whichever reason is in force.
const { isReadOnly: isRevisionReadOnly, blockedByPreview } = useProjectEditReadOnly();

// Weights are stored in kg; the page-level kg/ton switch controls how we display them here.
const isTon = computed(() => props.weightUnit === "ton");
const unitLabel = computed(() => (isTon.value ? "ton" : "kg"));
const toDisplay = (kg: number) => (isTon.value ? kg / 1000 : kg);
const toKg = (display: number) => (isTon.value ? display * 1000 : display);

const unitOptions = computed(() => [{ name: unitLabel.value, value: unitLabel.value }]);
const selectedUnit = ref(unitLabel.value);
const displayMaterialWeight = computed(() => (props.materialWeight !== undefined ? toDisplay(props.materialWeight) : undefined));

const isPercentageLocked = computed(() => !props.group.isCustom && props.wasteSource?.unit?.id === UnitId.PERCENTAGE);
const lockedPercentage = computed(() => (isPercentageLocked.value ? props.group.wasteType.value : null));

const editLabel = ref(props.pickup.label || "");
const editDate = ref(props.pickup.pickupDate ? String(props.pickup.pickupDate).slice(0, 10) : "");

const editPercentage = ref<number | null>(
  isPercentageLocked.value
    ? lockedPercentage.value
    : (() => {
        const w = props.pickup.customWeight ?? 0;
        if (props.materialWeight && props.materialWeight > 0) {
          return parseFloat(((w / props.materialWeight) * 100).toFixed(2));
        }
        return null;
      })()
);

const editWeight = ref<number | null>(
  isPercentageLocked.value && lockedPercentage.value && props.materialWeight
    ? parseFloat(toDisplay((lockedPercentage.value / 100) * props.materialWeight).toFixed(2))
    : toDisplay(props.pickup.customWeight ?? 0)
);

const handleWeightChange = (newWeight: number | null) => {
  editWeight.value = newWeight;
  if (props.materialWeight && props.materialWeight > 0 && newWeight !== null) {
    editPercentage.value = parseFloat(((toKg(newWeight) / props.materialWeight) * 100).toFixed(2));
  } else {
    editPercentage.value = null;
  }
};

const handlePercentageChange = (newPercentage: number | null) => {
  editPercentage.value = newPercentage;
  if (props.materialWeight && props.materialWeight > 0 && newPercentage !== null) {
    editWeight.value = parseFloat(toDisplay((newPercentage / 100) * props.materialWeight).toFixed(2));
  } else {
    editWeight.value = null;
  }
};

const isSaving = ref(false);
const isDeleting = ref(false);

const isMaterialMapping = computed(() => !!props.pickup.materialId);

const saveDisabled = computed(() => !editLabel.value || !editWeight.value || editWeight.value <= 0);

const wasteTypeIdFields = computed(() =>
  props.group.isCustom
    ? { projectWasteTypeCustomId: props.group.wasteType.id }
    : { materialWasteTypeId: props.group.wasteType.id }
);

const handleSave = async () => {
  if (blockedByPreview()) return;
  if (saveDisabled.value) return;
  isSaving.value = true;
  try {
    await props.onSubmit({
      id: props.pickup.id,
      projectId: String(route.params.id),
      label: editLabel.value || undefined,
      pickupDate: editDate.value || undefined,
      customWeight: toKg(editWeight.value ?? 0),
      materialId: props.pickup.materialId ?? undefined,
      materialWeightSnapshot: props.pickup.materialId ? props.materialWeight : undefined,
      ...wasteTypeIdFields.value,
    });
    modals.closeByName("WasteSortingEditModal");
  } finally {
    isSaving.value = false;
  }
};

const handleDelete = async () => {
  if (blockedByPreview()) return;
  isDeleting.value = true;
  try {
    await props.onDelete(props.pickup.id);
    modals.closeByName("WasteSortingEditModal");
  } finally {
    isDeleting.value = false;
  }
};
</script>

<template>
  <div class="flex h-[400px] flex-col gap-6">
    <h2 class="text-type-muted t-sh4 flex w-full items-center gap-2 pl-3">
      <font-awesome-icon
        :icon="isMaterialMapping ? 'fa-solid fa-cubes' : 'fa-solid fa-recycle text-app-green-500'"
        :class="isMaterialMapping ? 'text-slate-500' : 'text-app-green-500'"
        class="text-slate-400" />
      {{ pickup.materialId ? "Edit Material Waste" : "Edit Waste Sorting" }}
    </h2>
    <div class="flex flex-col gap-3 px-1">
      <div class="flex items-end gap-2">
        <TextInput
          v-model:value="editLabel"
          :disabled="!!pickup.materialId || isRevisionReadOnly"
          color="primary"
          :label="isMaterialMapping ? 'Material Name' : 'Name'"
          placeholder="Enter pickup name..."
          class="text-slate-600" />
      </div>

      <DateInput
        v-model="editDate"
        input-id="edit-waste-date-input"
        label="Pickup date"
        color="primary"
        :disabled="isRevisionReadOnly" />

      <div
        class="grid gap-3"
        :class="isMaterialMapping && materialWeight !== undefined ? 'grid-cols-[1fr_auto_auto_auto]' : 'grid-cols-[1fr_auto]'">
        <NumberInput
          :value="editWeight"
          label=" Weight"
          placeholder="0"
          color="primary"
          :positive="true"
          :disabled="isPercentageLocked || isRevisionReadOnly"
          class="text-slate-600"
          @update:value="handleWeightChange" />
        <SelectInputWithKeyValue
          :value="selectedUnit"
          :options="unitOptions"
          label="Unit"
          :is-disabled="isRevisionReadOnly"
          @update:value="selectedUnit = $event" />
        <div
          v-if="isMaterialMapping && materialWeight !== undefined"
          class="flex h-10 items-center self-end">
          <div class="h-6 w-px bg-slate-200"></div>
        </div>
        <NumberInput
          v-if="isMaterialMapping && materialWeight !== undefined"
          :value="editPercentage"
          label="Percentage"
          placeholder="0"
          color="primary"
          :max="100"
          :positive="true"
          :max-decimals="2"
          :disabled="isPercentageLocked || isRevisionReadOnly"
          class="text-slate-600"
          @update:value="handlePercentageChange" />
      </div>

      <div
        v-if="materialWeight !== undefined"
        class="t-xs flex items-center justify-end py-2 text-slate-500">
        <span
          >Total material weight:
          <strong class="text-slate-700">{{ format.decimalNumber(displayMaterialWeight ?? 0, 2) }} {{ unitLabel }}</strong></span
        >
      </div>
    </div>

    <div class="mt-auto flex justify-between px-1">
      <RevisionGatedButton
        label="Delete"
        color="destructive"
        :disabled="isSaving || isDeleting"
        :is-loading="isDeleting"
        :guard="useProjectEditReadOnly"
        @click="handleDelete" />

      <RevisionGatedButton
        :color="saveDisabled || isSaving || isDeleting ? 'disabled' : 'primary'"
        :disabled="saveDisabled || isSaving || isDeleting"
        :is-loading="isSaving"
        :guard="useProjectEditReadOnly"
        label="Save"
        @click="handleSave" />
    </div>
  </div>
</template>
